import { useContext } from "react";

import { SavedIcon } from "../../../StateManagment/SavedIcon";
import { db } from "../../../firebase/config.js";
import { useFirestore } from "../../../firebase/useFirestore";
import ToggleButton from "../../common/ToggleButton";
import resolveDefaultReminderPlan, { REMINDER_CHANNELS, MAX_DAYS_BEFORE } from "../../../helpers/adressHelpers/resolveDefaultReminderPlan";

/**
 * Instellingen → Ondersteuning → 'Standaard herinnering bij huisbezoek': de
 * hoofdschakelaar, het kanaal (e-mail, sms, beide) en hoeveel dagen van tevoren.
 *
 * SMS is alleen te kiezen als SMS-herinneringen voor de organisatie aanstaan
 * (het blok 'SMS-herinneringen' hieronder); de helper valt anders terug op
 * e-mail, en dat staat hier ook zo in de tekst. Een SMS gaat bovendien alleen
 * naar huishoudens die daar toestemming voor hebben gegeven (tabblad Gegevens).
 * Verzoek BuurtKlusBedrijf 2026-09-28: 'het huishouden automatisch een sms'.
 * AI-CONTEXT: See docs/features/default-housevisit-reminders.md
 *
 * @returns {JSX.Element}
 */
const DefaultHouseVisitReminderToggle = () => {
    const [, setSaved] = useContext(SavedIcon);
    const compagny = useFirestore("CompagnyMeta");

    /**
     * @param {string} docid
     * @param {string} kanaal - Een waarde uit REMINDER_CHANNELS
     */
    const channelHandler = (docid, kanaal) => {
        if (!REMINDER_CHANNELS.some((c) => c.value === kanaal)) return;
        db.collection("CompagnyMeta").doc(docid).update({ DefaultHouseVisitReminderChannel: kanaal })
            .then(() => setSaved("flex"));
    };

    /**
     * @param {string} docid
     * @param {string} waarde - Uit het getalveld
     */
    const daysHandler = (docid, waarde) => {
        const dagen = Number(waarde);
        if (!Number.isInteger(dagen) || dagen < 0 || dagen > MAX_DAYS_BEFORE) return;
        db.collection("CompagnyMeta").doc(docid).update({ DefaultHouseVisitReminderDays: dagen })
            .then(() => setSaved("flex"));
    };

    return (
        <div className="dashboard-container">
            <h2>Standaard herinnering bij huisbezoek</h2>
            <p>
                Zet aan om automatisch een herinnering voor de bewoner aan te maken zodra een huisbezoek wordt ingepland.
                Is er geen e-mailadres of telefoonnummer bekend, dan wordt die herinnering niet aangemaakt en zie je daar een melding van.
            </p>
            {compagny && compagny.map((comp) => {
                const plan = resolveDefaultReminderPlan(comp);
                const smsAan = comp.SmsRemindersEnabled === true || comp.SmsRemindersEnabled === 'true';
                return (
                    <div key={comp.ID}>
                        <div className="settings-general-module-settings-item-container">
                            <p>Automatisch herinnering aanmaken</p>
                            <ToggleButton
                                docid={comp.docid}
                                id={"DefaultHouseVisitReminder"}
                                collection={"CompagnyMeta"}
                                value={comp.DefaultHouseVisitReminder}
                            />
                        </div>
                        <div className="settings-general-module-settings-item-container">
                            <p>Per e-mail of SMS</p>
                            {/* Gestuurd (`value`), niet `defaultValue`: de opties hangen aan
                                een instelling uit een eigen Firestore-read.
                                AI-CONTEXT: See docs/pitfalls.md#ui-select-defaultvalue-loses-late-options */}
                            <select
                                aria-label="Per e-mail of SMS"
                                value={plan.channels.length === 2 ? 'both' : plan.channels[0]}
                                onChange={(e) => channelHandler(comp.docid, e.target.value)}
                            >
                                {REMINDER_CHANNELS
                                    .filter((c) => smsAan || c.value === 'email')
                                    .map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                        <div className="settings-general-module-settings-item-container">
                            <p>Aantal dagen van tevoren (0 = de ochtend van het bezoek)</p>
                            <input
                                type="number"
                                aria-label="Aantal dagen van tevoren"
                                min={0}
                                max={MAX_DAYS_BEFORE}
                                value={plan.daysBefore}
                                onChange={(e) => daysHandler(comp.docid, e.target.value)}
                            />
                        </div>
                        <p className="household-subtasks__hint">
                            {smsAan
                                ? "Een SMS gaat alleen naar huishoudens die daar toestemming voor hebben gegeven (tabblad 'Gegevens' van het huishouden). Elke verstuurde SMS kost geld."
                                : "SMS is hier pas te kiezen als 'SMS-herinneringen' hieronder aanstaat."}
                        </p>
                    </div>
                );
            })}
        </div>
    );
};

export default DefaultHouseVisitReminderToggle;
